import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { AppLayout, GlassCard, PageHeader, LoadingSpinner } from '../components/Layout';
import ScoreRing from '../components/ScoreRing';
import { ArrowRight, GraduationCap, Brain, Briefcase, Sparkles, Users, Target } from 'lucide-react';
import ModuleExplainer from '../components/ModuleExplainer';
import { useAuth } from '../context/AuthContext';
import { useCopy } from '../context/CopyContext';
import PageArrival from '../components/PageArrival';
import { Gauge as A1, BookOpen as A2, Brain as A3, Users as A4, FileText as A5, Search as A6 } from 'lucide-react';


const API = `${process.env.REACT_APP_BACKEND_URL}/api/career-growth`;

const ICONS = {
  academic: GraduationCap, skill: Brain, experience: Briefcase,
  professional: Sparkles, network: Users, job_search: Target,
};

// Each component owns a colour so the bars read as six distinct things, not one meter.
const HUES = {
  academic: ['#60a5fa', '#22d3ee'],
  skill: ['#22d3ee', '#34d399'],
  experience: ['#34d399', '#a3e635'],
  professional: ['#a78bfa', '#f472b6'],
  network: ['#f472b6', '#fb923c'],
  job_search: ['#fbbf24', '#f97316'],
};



export default function ReadinessPage() {
  const [arrived, setArrived] = useState(false);
  const { getAuthHeader } = useAuth();
  const { nav, isStudentCopy } = useCopy();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/readiness`, getAuthHeader());
      setData(res.data);
    } finally { setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <AppLayout><div className="flex items-center justify-center h-64"><LoadingSpinner size="lg" /></div></AppLayout>;
  }

  const d = data || {};
  const title = nav['/app/readiness'] || 'Career Launch Readiness';
  const student = isStudentCopy;

  if (!arrived) return (
    <AppLayout>
      <PageArrival
        testid="readiness-arrival"
        eyebrow={title}
        title="How ready are you, honestly?"
        subtitle={student
          ? "Six dimensions scored against where you're headed, your academic record, skills, experience, how you present yourself, who you know and whether you're actually working the process. Not a grade. A checklist of what's still missing."
          : "Six dimensions scored against the job you actually want, academic, skills, experience, professional presence, network and job search. Not a grade. A checklist of what is still missing."}
        accent="#f472b6"
        second="#22d3ee"
        ctaLabel="Score my readiness"
        narration={`This is ${title}. It scores six things against where you are headed: your academic record, your skills, your experience, your professional presence, your network, and how well you are actually running the process. The score is not the point, the gaps are. Each dimension tells you what is missing and the single next action that raises it most.`}
        facts={[
          { icon: A1, head: "Six dimensions", sub: "Scored against your target, not in general" },
          { icon: A5, head: "Gaps named", sub: "Every score comes with what is missing" },
          { icon: A6, head: "One next action", sub: "The move that raises the score most" }
        ]}
        modules={[
          { icon: A2, label: "Academic", line: "Grades, courses and credentials against what the role expects", hue: "#22d3ee" },
          { icon: A3, label: "Skills", line: "What you have evidenced versus what the job asks", hue: "#34d399" },
          { icon: A5, label: "Experience", line: "Internships, work, projects and service that count", hue: "#fbbf24" },
          { icon: A4, label: "Network", line: "Who you know, and the relationships worth building", hue: "#a78bfa" },
          { icon: A6, label: student ? "Applying" : "Job search", line: student ? "Whether your applications, deadlines and follow-ups are actually moving" : "Whether you are actually running a process, or hoping", hue: "#f472b6" },
          { icon: A1, label: "Professional presence", line: "How you look to someone who searches your name", hue: "#60a5fa" }
        ]}
        onStart={() => { setArrived(true); window.scrollTo({ top: 0 }); }}
      />
    </AppLayout>
  );

  return (
    <AppLayout>
      <PageHeader
        title={title}
        description={student
          ? "One number that moves as you do, built from the six things colleges and employers actually weigh."
          : "One number that moves as you do, built from six things employers actually weigh."}
      />

      <ModuleExplainer id="readiness" />

      <GlassCard className="mb-6" data-testid="readiness-card">
        <div className="flex flex-col md:flex-row items-center gap-8">
          <ScoreRing value={d.overall} label={d.band} palette="cyan" testId="readiness-overall" />
          <div className="flex-1 w-full">
            <p className="text-[11px] uppercase tracking-wider text-slate-300">Measured against</p>
            <p className="text-lg font-semibold text-white mb-4">{d.target || 'No target career set yet'}</p>
            <div className="space-y-3">
              {(d.components || []).map(c => {
                const Icon = ICONS[c.key] || Target;
                const [from, to] = HUES[c.key] || HUES.skill;
                return (
                  <div key={c.key} data-testid={`component-${c.key}`}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="flex items-center gap-2 text-slate-200">
                        <Icon className="w-3.5 h-3.5" style={{ color: from }} />{c.label}
                      </span>
                      <span className="tabular-nums font-semibold" style={{ color: from }}>{c.score}</span>
                    </div>
                    <div className="h-2 bg-slate-700/70 rounded-full overflow-hidden">
                      <div className="h-full rounded-full"
                        style={{
                          width: `${c.score}%`,
                          background: `linear-gradient(90deg, ${from}, ${to})`,
                          boxShadow: `0 0 12px ${from}66`,
                          transition: 'width 1s cubic-bezier(.22,1,.36,1)',
                        }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-slate-300 mt-4">{d.note}</p>
          </div>
        </div>
      </GlassCard>

      <h3 className="text-base md:text-lg font-semibold text-white mb-3">Do these next, they move the score most</h3>
      <div className="grid md:grid-cols-3 gap-4">
        {(d.top_actions || []).map((a, i) => {
          const key = Object.keys(ICONS).find(k => (d.components || []).find(c => c.key === k && c.label === a.component));
          const [from, to] = HUES[key] || HUES.skill;
          return (
            <Link key={i} to={a.path} data-testid={`readiness-action-${i}`}
              className="group relative overflow-hidden p-5 rounded-xl border border-slate-700 bg-slate-800/40 hover:bg-slate-800/70 transition-colors duration-200">
              <span className="absolute inset-x-0 top-0 h-[3px]" style={{ background: `linear-gradient(90deg, ${from}, ${to})` }} />
              <span className="absolute -top-16 -right-16 w-40 h-40 rounded-full blur-3xl opacity-25 group-hover:opacity-45 transition-opacity duration-300"
                style={{ background: from }} />
              <div className="relative flex items-center justify-between mb-3">
                <span className="text-[10px] uppercase tracking-[0.18em]" style={{ color: from }}>{a.component}</span>
                <span className="text-xs tabular-nums text-slate-300">{a.current}/100</span>
              </div>
              <p className="relative text-sm font-semibold text-white leading-snug">{a.action}</p>
              <p className="relative text-xs text-slate-300 mt-2">{a.why}</p>
              <span className="relative mt-4 inline-flex items-center gap-1 text-xs group-hover:gap-2 transition-all duration-200" style={{ color: from }}>
                Go there <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </Link>
          );
        })}
      </div>
    </AppLayout>
  );
}
